import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, NavLink, useLocation, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { 
  LayoutDashboard, 
  FileText, 
  Settings, 
  ClipboardList, 
  Menu, 
  X,
  TrendingUp,
  Mail,
  FileCheck,
  Users,
  ChevronRight,
  ExternalLink,
  Download,
  RefreshCw,
  CheckCircle2,
  Clock,
  AlertCircle,
  Loader2,
  Link as LinkIcon,
  Copy,
  FileQuestion
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Badge } from "./components/ui/badge";
import { Separator } from "./components/ui/separator";
import { ScrollArea } from "./components/ui/scroll-area";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./components/ui/table";
import { Slider } from "./components/ui/slider";
import { Progress } from "./components/ui/progress";
import { Questionnaire } from "./Questionnaire";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Logo Component
const Logo = () => (
  <div className="flex items-center gap-3">
    <img 
      src="https://customer-assets.emergentagent.com/job_report-builder-pro-4/artifacts/lejhtfu6_Untitled%20design.png" 
      alt="Food Kindle" 
      className="h-10 w-10 rounded-md"
    />
    <div>
      <h1 className="font-bold text-lg text-foreground font-['Manrope']">Food Kindle</h1>
      <p className="text-xs text-muted-foreground">Growth Diagnostics</p>
    </div>
  </div>
);

// Sidebar Component
const Sidebar = ({ isOpen, onClose }) => {
  const location = useLocation();
  
  const navItems = [
    { path: "/", icon: LayoutDashboard, label: "Dashboard" },
    { path: "/simulator", icon: ClipboardList, label: "Test Simulator" },
    { path: "/submissions", icon: FileText, label: "Submissions" },
    { path: "/settings", icon: Settings, label: "Settings" },
  ];

  const questionnaireUrl = `${window.location.origin}/diagnostic`;

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}
      
      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 z-50 h-full w-64 bg-white border-r border-border
        transform transition-transform duration-200 ease-in-out
        lg:translate-x-0 lg:static lg:z-auto
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-border">
            <Logo />
            <button 
              onClick={onClose}
              className="lg:hidden p-1 hover:bg-secondary rounded-md transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          
          {/* Navigation */}
          <nav className="flex-1 p-4">
            <ul className="space-y-1">
              {navItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <li key={item.path}>
                    <NavLink
                      to={item.path}
                      onClick={onClose}
                      className={`
                        flex items-center gap-3 px-3 py-2.5 rounded-md font-medium text-sm
                        transition-all duration-200
                        ${isActive 
                          ? 'bg-primary text-primary-foreground' 
                          : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                        }
                      `}
                      data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                    >
                      <item.icon className="h-4 w-4" />
                      {item.label}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </nav>
          
          {/* Footer */}
          <div className="p-4 border-t border-border">
            <a 
              href="/diagnostic" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-xs text-emerald-600 hover:text-emerald-700 mb-2"
            >
              <FileQuestion className="h-3 w-3" />
              Open Public Questionnaire
              <ExternalLink className="h-3 w-3" />
            </a>
            <p className="text-xs text-muted-foreground text-center">
              © 2026 Food Kindle
            </p>
          </div>
        </div>
      </aside>
    </>
  );
};

// Dashboard Page
const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
      toast.error("Failed to load dashboard stats");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-emerald-500 hover:bg-emerald-600"><CheckCircle2 className="h-3 w-3 mr-1" /> Completed</Badge>;
      case 'processing':
        return <Badge className="bg-amber-500 hover:bg-amber-600"><Clock className="h-3 w-3 mr-1" /> Processing</Badge>;
      case 'failed':
        return <Badge variant="destructive"><AlertCircle className="h-3 w-3 mr-1" /> Failed</Badge>;
      default:
        return <Badge variant="secondary"><Clock className="h-3 w-3 mr-1" /> Pending</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="dashboard">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight font-['Manrope']">Dashboard</h1>
        <p className="text-muted-foreground mt-1">Restaurant Growth Diagnostic Overview</p>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className="border-border hover:border-primary/20 transition-colors">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Submissions</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold" data-testid="stat-total-submissions">{stats?.total_submissions || 0}</div>
            <p className="text-xs text-muted-foreground">All time assessments</p>
          </CardContent>
        </Card>

        <Card className="border-border hover:border-primary/20 transition-colors">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Reports Generated</CardTitle>
            <FileCheck className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold" data-testid="stat-completed-reports">{stats?.completed_reports || 0}</div>
            <p className="text-xs text-muted-foreground">PDF reports created</p>
          </CardContent>
        </Card>

        <Card className="border-border hover:border-primary/20 transition-colors">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Emails Sent</CardTitle>
            <Mail className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold" data-testid="stat-emails-sent">{stats?.emails_sent || 0}</div>
            <p className="text-xs text-muted-foreground">Reports delivered</p>
          </CardContent>
        </Card>

        <Card className="border-border hover:border-primary/20 transition-colors">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg. Score</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold" data-testid="stat-avg-score">{stats?.avg_score || 0}/120</div>
            <p className="text-xs text-muted-foreground">Average assessment score</p>
          </CardContent>
        </Card>
      </div>

      {/* Recent Submissions */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Recent Submissions</CardTitle>
              <CardDescription>Latest assessment requests</CardDescription>
            </div>
            <NavLink to="/submissions">
              <Button variant="ghost" size="sm">
                View All <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </NavLink>
          </div>
        </CardHeader>
        <CardContent>
          {stats?.recent_submissions?.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Restaurant</TableHead>
                  <TableHead>City</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Email Sent</TableHead>
                  <TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {stats.recent_submissions.map((submission) => (
                  <TableRow key={submission.id} data-testid={`submission-row-${submission.id}`}>
                    <TableCell className="font-medium">{submission.restaurant_name}</TableCell>
                    <TableCell>{submission.city}</TableCell>
                    <TableCell>{getStatusBadge(submission.status)}</TableCell>
                    <TableCell>
                      {submission.email_sent ? (
                        <Badge variant="outline" className="text-emerald-600 border-emerald-600">Sent</Badge>
                      ) : (
                        <Badge variant="outline">Pending</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {new Date(submission.timestamp).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No submissions yet</p>
              <p className="text-sm">Use the Test Simulator to create your first assessment</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Test Simulator Page
const TestSimulator = () => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    restaurant_name: "",
    city: "",
    email: "",
    phone: "",
    // Discovery
    q1_google_profile: 3,
    q2_instagram_posting: 3,
    q3_paid_ads: 2,
    q4_influencer_marketing: 2,
    q5_offline_visibility: 3,
    q6_aggregator_dependence: 3,
    // Conversion
    q7_store_frontage: 3,
    q8_google_rating: 4,
    q9_hero_products: 3,
    q10_offers_compelling: 2,
    q11_staff_recommendations: 2,
    q12_service_time: 3,
    // Revenue
    q13_aov_trend: 3,
    q14_bundles_combos: 2,
    q15_menu_engineering: 2,
    q16_premium_items: 3,
    q17_staff_upsell: 2,
    q18_sku_profitability: 2,
    // Retention
    q19_customer_data: 2,
    q20_whatsapp_campaigns: 1,
    q21_repeat_visitors: 3,
    q22_loyalty_programs: 2,
    q23_review_requests: 2,
    q24_repeat_visit_tracking: 2,
  });

  const questionLabels = {
    q1_google_profile: "Google Business Profile visibility",
    q2_instagram_posting: "Instagram posting consistency",
    q3_paid_ads: "Paid advertising",
    q4_influencer_marketing: "Influencer collaborations",
    q5_offline_visibility: "Local offline visibility",
    q6_aggregator_dependence: "Aggregator dependence (lower is better)",
    q7_store_frontage: "Store frontage attractiveness",
    q8_google_rating: "Average Google rating",
    q9_hero_products: "Hero products clarity",
    q10_offers_compelling: "Offers compelling",
    q11_staff_recommendations: "Staff recommendations",
    q12_service_time: "Service speed",
    q13_aov_trend: "Average Order Value trend",
    q14_bundles_combos: "Bundles/Combos offerings",
    q15_menu_engineering: "Menu engineering",
    q16_premium_items: "Premium items highlighting",
    q17_staff_upsell: "Staff upselling",
    q18_sku_profitability: "SKU profitability tracking",
    q19_customer_data: "Customer data collection",
    q20_whatsapp_campaigns: "WhatsApp campaigns",
    q21_repeat_visitors: "Repeat visitor percentage",
    q22_loyalty_programs: "Loyalty programs",
    q23_review_requests: "Review requests",
    q24_repeat_visit_tracking: "Repeat visit tracking",
  };

  const sections = [
    { title: "Discovery Engine", keys: ["q1_google_profile", "q2_instagram_posting", "q3_paid_ads", "q4_influencer_marketing", "q5_offline_visibility", "q6_aggregator_dependence"] },
    { title: "Conversion Engine", keys: ["q7_store_frontage", "q8_google_rating", "q9_hero_products", "q10_offers_compelling", "q11_staff_recommendations", "q12_service_time"] },
    { title: "Revenue Engine", keys: ["q13_aov_trend", "q14_bundles_combos", "q15_menu_engineering", "q16_premium_items", "q17_staff_upsell", "q18_sku_profitability"] },
    { title: "Retention Engine", keys: ["q19_customer_data", "q20_whatsapp_campaigns", "q21_repeat_visitors", "q22_loyalty_programs", "q23_review_requests", "q24_repeat_visit_tracking"] },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.restaurant_name || !formData.city || !formData.email) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/test-submission`, formData);
      toast.success(`Submission created! ID: ${response.data.id}`);
      // Reset form
      setFormData({
        ...formData,
        restaurant_name: "",
        city: "",
        email: "",
        phone: "",
      });
    } catch (error) {
      console.error("Submission failed:", error);
      toast.error("Failed to create submission");
    } finally {
      setLoading(false);
    }
  };

  const calculateSectionScore = (keys) => {
    return keys.reduce((sum, key) => sum + formData[key], 0);
  };

  const totalScore = Object.keys(formData)
    .filter(key => key.startsWith('q'))
    .reduce((sum, key) => sum + formData[key], 0);

  return (
    <div className="space-y-8 animate-fade-in" data-testid="test-simulator">
      <div>
        <h1 className="text-3xl font-bold tracking-tight font-['Manrope']">Test Simulator</h1>
        <p className="text-muted-foreground mt-1">Simulate Zoho form submissions for testing</p>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Restaurant Info */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Restaurant Information</CardTitle>
            <CardDescription>Basic details about the restaurant</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="restaurant_name">Restaurant Name *</Label>
                <Input
                  id="restaurant_name"
                  placeholder="Enter restaurant name"
                  value={formData.restaurant_name}
                  onChange={(e) => setFormData({ ...formData, restaurant_name: e.target.value })}
                  data-testid="input-restaurant-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="city">City *</Label>
                <Input
                  id="city"
                  placeholder="Enter city"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  data-testid="input-city"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  data-testid="input-email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  placeholder="Enter phone (optional)"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  data-testid="input-phone"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Score Preview */}
        <Card className="mb-6 bg-slate-900 text-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">Total Score Preview</p>
                <p className="text-4xl font-bold">{totalScore}/120</p>
              </div>
              <div className="grid grid-cols-4 gap-4 text-center">
                {sections.map((section, idx) => (
                  <div key={idx}>
                    <p className="text-xs text-slate-400">{section.title.replace(' Engine', '')}</p>
                    <p className="text-lg font-semibold">{calculateSectionScore(section.keys)}/30</p>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Question Sections */}
        {sections.map((section, sectionIdx) => (
          <Card key={sectionIdx} className="mb-6">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>{section.title}</CardTitle>
                  <CardDescription>Score: {calculateSectionScore(section.keys)}/30</CardDescription>
                </div>
                <Progress value={(calculateSectionScore(section.keys) / 30) * 100} className="w-24" />
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {section.keys.map((key) => (
                <div key={key} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">{questionLabels[key]}</Label>
                    <span className="text-sm font-medium bg-secondary px-2 py-0.5 rounded">{formData[key]}/5</span>
                  </div>
                  <Slider
                    value={[formData[key]]}
                    onValueChange={(value) => setFormData({ ...formData, [key]: value[0] })}
                    min={1}
                    max={5}
                    step={1}
                    className="w-full"
                    data-testid={`slider-${key}`}
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>1 - Poor</span>
                    <span>2</span>
                    <span>3</span>
                    <span>4</span>
                    <span>5 - Excellent</span>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        ))}

        <Button 
          type="submit" 
          size="lg" 
          className="w-full"
          disabled={loading}
          data-testid="submit-button"
        >
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Processing...
            </>
          ) : (
            "Submit Test Assessment"
          )}
        </Button>
      </form>
    </div>
  );
};

// Submissions Page
const Submissions = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const fetchSubmissions = async () => {
    try {
      const response = await axios.get(`${API}/submissions`);
      setSubmissions(response.data.submissions);
      setTotal(response.data.total);
    } catch (error) {
      console.error("Failed to fetch submissions:", error);
      toast.error("Failed to load submissions");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (submissionId, restaurantName) => {
    try {
      const response = await axios.get(`${API}/reports/${submissionId}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${restaurantName}_Growth_Scorecard.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("PDF downloaded successfully");
    } catch (error) {
      console.error("Download failed:", error);
      toast.error("Failed to download PDF");
    }
  };

  const handleResendEmail = async (submissionId) => {
    try {
      await axios.post(`${API}/submissions/${submissionId}/resend-email`);
      toast.success("Email sent successfully");
      fetchSubmissions();
    } catch (error) {
      console.error("Resend failed:", error);
      toast.error(error.response?.data?.detail || "Failed to send email");
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-emerald-500 hover:bg-emerald-600"><CheckCircle2 className="h-3 w-3 mr-1" /> Completed</Badge>;
      case 'processing':
        return <Badge className="bg-amber-500 hover:bg-amber-600"><Clock className="h-3 w-3 mr-1" /> Processing</Badge>;
      case 'failed':
        return <Badge variant="destructive"><AlertCircle className="h-3 w-3 mr-1" /> Failed</Badge>;
      default:
        return <Badge variant="secondary"><Clock className="h-3 w-3 mr-1" /> Pending</Badge>;
    }
  };

  const getRatingBadge = (rating) => {
    if (!rating) return null;
    const colors = {
      'Strong Growth System': 'bg-emerald-500',
      'Moderate System': 'bg-blue-500',
      'Weak System': 'bg-amber-500',
      'Structural Problem': 'bg-red-500'
    };
    return (
      <Badge className={colors[rating] || 'bg-gray-500'}>
        {rating}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="submissions-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Manrope']">Submissions</h1>
          <p className="text-muted-foreground mt-1">{total} total submissions</p>
        </div>
        <Button onClick={fetchSubmissions} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <ScrollArea className="h-[600px]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Restaurant</TableHead>
                  <TableHead>City</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Score</TableHead>
                  <TableHead>Rating</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {submissions.length > 0 ? (
                  submissions.map((submission) => (
                    <TableRow key={submission.id} data-testid={`submission-${submission.id}`}>
                      <TableCell className="font-medium">{submission.restaurant_name}</TableCell>
                      <TableCell>{submission.city}</TableCell>
                      <TableCell className="text-muted-foreground">{submission.email}</TableCell>
                      <TableCell>
                        {submission.scores?.total_score ? (
                          <span className="font-semibold">{submission.scores.total_score}/120</span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {getRatingBadge(submission.scores?.overall_rating)}
                      </TableCell>
                      <TableCell>{getStatusBadge(submission.status)}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {new Date(submission.timestamp).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          {submission.status === 'completed' && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDownload(submission.id, submission.restaurant_name)}
                                data-testid={`download-btn-${submission.id}`}
                              >
                                <Download className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleResendEmail(submission.id)}
                                data-testid={`resend-btn-${submission.id}`}
                              >
                                <Mail className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      No submissions found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
};

// Settings Page
const SettingsPage = () => {
  const [searchParams] = useSearchParams();
  const [gmailStatus, setGmailStatus] = useState({ connected: false, email: null });
  const [settings, setSettings] = useState({ webhook_url: "" });
  const [loading, setLoading] = useState(true);
  const [connectingGmail, setConnectingGmail] = useState(false);

  useEffect(() => {
    fetchSettings();
    
    // Check for Gmail connection status from URL
    if (searchParams.get('gmail') === 'connected') {
      toast.success("Gmail connected successfully!");
    }
  }, [searchParams]);

  const fetchSettings = async () => {
    try {
      const [gmailRes, settingsRes] = await Promise.all([
        axios.get(`${API}/oauth/gmail/status`),
        axios.get(`${API}/settings`)
      ]);
      setGmailStatus(gmailRes.data);
      setSettings(settingsRes.data);
    } catch (error) {
      console.error("Failed to fetch settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleConnectGmail = async () => {
    setConnectingGmail(true);
    try {
      const response = await axios.get(`${API}/oauth/gmail/login`);
      window.location.href = response.data.auth_url;
    } catch (error) {
      console.error("Gmail connect failed:", error);
      toast.error(error.response?.data?.detail || "Failed to initiate Gmail connection");
      setConnectingGmail(false);
    }
  };

  const handleDisconnectGmail = async () => {
    try {
      await axios.post(`${API}/oauth/gmail/disconnect`);
      setGmailStatus({ connected: false, email: null });
      toast.success("Gmail disconnected");
    } catch (error) {
      console.error("Disconnect failed:", error);
      toast.error("Failed to disconnect Gmail");
    }
  };

  const copyWebhookUrl = () => {
    navigator.clipboard.writeText(settings.webhook_url);
    toast.success("Webhook URL copied to clipboard");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="settings-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight font-['Manrope']">Settings</h1>
        <p className="text-muted-foreground mt-1">Configure integrations and preferences</p>
      </div>

      {/* Gmail Integration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Gmail Integration
          </CardTitle>
          <CardDescription>
            Connect Gmail to send PDF reports to respondents
          </CardDescription>
        </CardHeader>
        <CardContent>
          {gmailStatus.connected ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-emerald-100 flex items-center justify-center">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <p className="font-medium">Connected</p>
                  <p className="text-sm text-muted-foreground">{gmailStatus.email}</p>
                </div>
              </div>
              <Button variant="destructive" onClick={handleDisconnectGmail} data-testid="disconnect-gmail-btn">
                Disconnect
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800">
                  <strong>Note:</strong> Gmail integration requires Google Cloud Console credentials 
                  (GOOGLE_CLIENT_ID and GOOGLE_CLIENT_SECRET) to be configured in the backend environment.
                </p>
              </div>
              <Button 
                onClick={handleConnectGmail} 
                disabled={connectingGmail}
                data-testid="connect-gmail-btn"
              >
                {connectingGmail ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Connecting...
                  </>
                ) : (
                  <>
                    <Mail className="mr-2 h-4 w-4" />
                    Connect Gmail
                  </>
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Questionnaire Link Card */}
      <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileQuestion className="h-5 w-5" />
            Public Questionnaire
          </CardTitle>
          <CardDescription className="text-emerald-100">
            Share this link with restaurant owners to collect assessments
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2">
            <Input 
              value={`${window.location.origin}/diagnostic`}
              readOnly 
              className="bg-white/20 border-white/30 text-white placeholder:text-white/50 font-mono text-sm"
              data-testid="questionnaire-url-input"
            />
            <Button 
              variant="secondary" 
              onClick={() => {
                navigator.clipboard.writeText(`${window.location.origin}/diagnostic`);
                toast.success("Link copied to clipboard");
              }}
              className="bg-white text-emerald-600 hover:bg-emerald-50"
              data-testid="copy-questionnaire-url-btn"
            >
              <Copy className="h-4 w-4" />
            </Button>
          </div>
          <a 
            href="/diagnostic" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 mt-4 text-sm text-white/90 hover:text-white"
          >
            Preview Questionnaire
            <ExternalLink className="h-4 w-4" />
          </a>
        </CardContent>
      </Card>

      {/* Webhook Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <LinkIcon className="h-5 w-5" />
            Zoho Forms Webhook
          </CardTitle>
          <CardDescription>
            Configure this URL in your Zoho Forms to receive submissions
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Input 
                value={settings.webhook_url} 
                readOnly 
                className="font-mono text-sm"
                data-testid="webhook-url-input"
              />
              <Button variant="outline" onClick={copyWebhookUrl} data-testid="copy-webhook-btn">
                <Copy className="h-4 w-4" />
              </Button>
            </div>
            <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
              <p className="text-sm font-medium mb-2">Setup Instructions:</p>
              <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                <li>Go to your Zoho Forms settings</li>
                <li>Navigate to Integrations → Webhooks</li>
                <li>Add a new webhook with the URL above</li>
                <li>Set the method to POST</li>
                <li>Map form fields to match the expected format</li>
              </ol>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Zoho Form Link */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ExternalLink className="h-5 w-5" />
            Zoho Form
          </CardTitle>
          <CardDescription>
            Your Restaurant Growth Diagnostic questionnaire
          </CardDescription>
        </CardHeader>
        <CardContent>
          <a 
            href="https://forms.zoho.in/randheerbhardwajgm1/form/RestaurantGrowthDiagnostic/builder"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-primary hover:underline"
          >
            Open Zoho Form Builder
            <ExternalLink className="h-4 w-4" />
          </a>
        </CardContent>
      </Card>
    </div>
  );
};

// Main Layout
const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background">
      <div className="grain-overlay" />
      
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      
      <div className="lg:pl-64">
        {/* Mobile header */}
        <header className="sticky top-0 z-30 flex items-center gap-4 border-b border-border bg-background/95 backdrop-blur px-4 py-3 lg:hidden">
          <button 
            onClick={() => setSidebarOpen(true)}
            className="p-2 hover:bg-secondary rounded-md transition-colors"
            data-testid="mobile-menu-btn"
          >
            <Menu className="h-5 w-5" />
          </button>
          <Logo />
        </header>
        
        {/* Main content */}
        <main className="p-6 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <Toaster position="top-right" richColors />
      <Routes>
        {/* Public questionnaire - no layout */}
        <Route path="/diagnostic" element={<Questionnaire />} />
        
        {/* Admin routes with layout */}
        <Route path="/*" element={
          <Layout>
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/simulator" element={<TestSimulator />} />
              <Route path="/submissions" element={<Submissions />} />
              <Route path="/settings" element={<SettingsPage />} />
            </Routes>
          </Layout>
        } />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
